import { For, Show, createMemo, createSignal, type Component, type JSX } from 'solid-js';
import { Text } from '~ui/text';
import { Button } from '~ui/button';
import { Input } from '~ui/input';
import { Select } from '~ui/select';
import { NumberBox } from '~ui/field';
import { PlusIcon, TrashIcon } from '~ui/icons';
import { formatCurrency, formatPercent } from '~/lib/format';
import { LOAN_KINDS, LOAN_KIND_LABEL, loanAvailable, type Loan } from '~/data/portfolios';
import { addLoan, allocatedTotal, loans, removeLoan, setLoanBalance, setLoanField, setLoanLimit, setLoanRate } from '~/stores/portfolio-store';

// The facilities you can borrow against, and the only place they are edited.
//
// A loan used to be one of exactly two rows rendered from a constant, which
// could not describe two HELOCs, a second mortgage, or an unsecured line you
// have not drawn. These are rows: added, changed and deleted.
//
// Five columns, and each is something only you know — who lends it, what it is,
// what it costs, what you may draw and what you have drawn. Everything else on
// the Loans page is derived from these five and from the trades that named the
// loan, which is why nothing here is a figure the app worked out.

const HEAD = 'flex h-row shrink-0 items-center border-b bg-muted px-2 whitespace-nowrap';

const Head: Component<{ children: JSX.Element; class?: string; align?: 'right' }> = (props) => (
    <div class={`flex min-w-0 items-center ${props.align === 'right' ? 'justify-end' : ''} ${props.class ?? ''}`}>
        <Text tone='muted' class='truncate'>
            {props.children}
        </Text>
    </div>
);

const Row: Component<{ loan: Loan }> = (props) => {
    // Asks once rather than firing on a click. Deleting a facility does not
    // delete the trades it funded — the positions stay held, with their cost
    // base — but it does take the record of how they were paid for, and with it
    // every deductibility figure that record supported.
    const [confirming, setConfirming] = createSignal(false);
    const invested = createMemo(() => allocatedTotal(props.loan));

    return (
        <div class='flex h-row items-center gap-2 border-b px-2 whitespace-nowrap hover:bg-muted'>
            <div class='flex w-40 min-w-0 items-center'>
                <Input bare value={props.loan.issuer} placeholder='Bank' class='min-w-0 py-0!' onInput={(e) => setLoanField(props.loan.id, 'issuer', e.currentTarget.value)} />
            </div>
            <div class='flex w-40 min-w-0 items-center'>
                <Input bare value={props.loan.label} placeholder='Name' class='min-w-0 py-0!' onInput={(e) => setLoanField(props.loan.id, 'label', e.currentTarget.value)} />
            </div>
            {/* A list, not free text, and margin is why: a margin loan is secured
                against the securities in the margin account, so the app refuses
                to let it fund a TFSA or an RRSP. It can only do that while the
                kind is something it can read. */}
            <Select
                class='w-36 shrink-0'
                options={LOAN_KINDS.map((k) => ({ value: k, label: LOAN_KIND_LABEL[k] }))}
                value={props.loan.kind}
                onChange={(e) => setLoanField(props.loan.id, 'kind', e.currentTarget.value as Loan['kind'])}
            />
            <NumberBox value={props.loan.rate} onValue={(n) => setLoanRate(props.loan.id, n)} suffix='%' width='w-24 shrink-0' border='l' />
            {/* Zero means no limit rather than a facility you may draw nothing
                from — a term loan is fully drawn by construction, and a limit of
                zero would report it as 0% used. */}
            <NumberBox value={props.loan.limit ?? 0} onValue={(n) => setLoanLimit(props.loan.id, n <= 0 ? null : n)} prefix='$' width='w-32 shrink-0' border='l' />
            <NumberBox value={props.loan.balance} onValue={(n) => setLoanBalance(props.loan.id, n)} prefix='$' width='w-32 shrink-0' border='l' />

            {/* Derived, and the reason the two boxes beside it are worth typing:
                headroom is the question a limit is for. Blank where there is no
                limit to measure against. */}
            <div class='flex w-28 shrink-0 items-center justify-end'>
                <Text tone='muted'>{loanAvailable(props.loan) == null ? '—' : formatCurrency(loanAvailable(props.loan))}</Text>
            </div>

            <div class='flex w-7 shrink-0 items-center justify-center'>
                <Button
                    size='fill'
                    icon={<TrashIcon />}
                    class={confirming() ? 'bg-red text-background hover:bg-red!' : ''}
                    title={
                        confirming()
                            ? 'Delete for good?'
                            : invested() > 0
                              ? `Delete — ${formatCurrency(invested())} of trades will lose their funding record`
                              : `Delete ${props.loan.label}`
                    }
                    onClick={() => {
                        if (!confirming()) {
                            setConfirming(true);
                            return;
                        }
                        void removeLoan(props.loan.id);
                    }}
                />
            </div>
        </div>
    );
};

export const LoanTable: Component = () => {
    const [adding, setAdding] = createSignal(false);

    return (
        <>
            <div class={HEAD}>
                <Head class='w-40'>Issuer</Head>
                <Head class='w-40 pl-2'>Name</Head>
                <Head class='w-36 pl-2'>Type</Head>
                <Head class='w-24 pl-2' align='right'>
                    Rate
                </Head>
                <Head class='w-32 pl-2' align='right'>
                    Limit
                </Head>
                <Head class='w-32 pl-2' align='right'>
                    Drawn
                </Head>
                <Head class='w-28 pl-2' align='right'>
                    Available
                </Head>
                <div class='w-7 shrink-0' />
            </div>

            <Show
                when={loans().length > 0}
                fallback={
                    <div class='border-b px-2 py-3'>
                        <Text tone='muted'>Nothing yet. Add a facility you could borrow against — you do not have to draw on it for the app to plan with it.</Text>
                    </div>
                }>
                <For each={loans()}>{(loan) => <Row loan={loan} />}</For>
            </Show>

            <div class='flex h-row items-center border-b px-1'>
                <Button
                    size='fill'
                    icon={<PlusIcon />}
                    label={adding() ? 'Adding' : 'Add a loan'}
                    disabled={adding()}
                    title='Add a borrowing facility'
                    onClick={() => {
                        setAdding(true);
                        void addLoan().finally(() => setAdding(false));
                    }}
                />
            </div>

            {/* Said once, here, rather than as a hint on every row: the rule is
                about the kind rather than about any one facility, and it is the
                only thing on this table the app will act on without being
                asked. */}
            <div class='px-2 py-3'>
                <Text tone='muted'>
                    A margin loan is secured against the securities in the margin account itself, so the app will not let one fund a TFSA or an RRSP — the broker has no claim on assets inside a
                    registered account and will not lend against them. Interest is deductible only on the share that went into a taxable account, which the Loans page works out from your trades.
                </Text>
            </div>
        </>
    );
};

export default LoanTable;
